import React, { useMemo } from 'react';
import { Stack } from '@mui/material';
import { FormikContextType, useFormikContext } from 'formik';
import { InputQuantity, InputSelect } from '@farmshare/ui-components';
import moment, { Moment } from 'moment';
import { inspectionLevelHelper } from '@farmshare/utils';
import {
  EnumProcessorSchedulingAnimalHeadsInspectionLevel,
  EnumProcessorSchedulingAnimalSpecies,
} from '../../../../lib/graphql';
import { filterGameSpecies, useWildGameEnabled } from 'features/wild-game';
import { QuickScheduleMiniMonth } from '../quick-schedule-mini-month';
import { QuickScheduleForm } from './types';
import { useAutoProgressOnBlock } from './use-auto-progress';

interface ScheduleDetailsStepAnimalSetting {
  species?: string | null;
  isEnabled?: boolean | null;
  inspectionLevels?: (string | null)[] | null;
}

interface ScheduleDetailsStepProcessorSettings {
  animalSettings?: (ScheduleDetailsStepAnimalSetting | null)[] | null;
}

export function ScheduleDetailsStep({
  vendorId,
  processorSettings = undefined,
  onNext = undefined,
}: {
  vendorId: string;
  processorSettings?: ScheduleDetailsStepProcessorSettings;
  onNext?: () => void;
}) {
  let values: QuickScheduleForm | undefined;
  let setFieldValue:
    | FormikContextType<QuickScheduleForm>['setFieldValue']
    | undefined;

  try {
    const formikContext = useFormikContext<QuickScheduleForm>();
    values = formikContext?.values;
    setFieldValue = formikContext?.setFieldValue;
  } catch {
    // Formik context not available yet
  }

  // Auto-progress when block is selected
  useAutoProgressOnBlock(onNext);

  const wildGameEnabled = useWildGameEnabled();

  // Filter species based on enabled animal settings
  const speciesOptions = useMemo(() => {
    const enabledSpecies =
      processorSettings?.animalSettings
        ?.filter((setting) => setting?.isEnabled)
        .map((setting) => setting?.species) || [];

    // Only offer species the processor has explicitly enabled. When none are
    // enabled the list is intentionally empty — the processor can't process
    // anything, so we must not fall back to showing every species.
    return filterGameSpecies(
      Object.values(EnumProcessorSchedulingAnimalSpecies),
      wildGameEnabled,
    )
      .filter((species) => enabledSpecies.includes(species))
      .map((species: string) => ({
        label: species.charAt(0).toUpperCase() + species.slice(1),
        value: species,
      }));
  }, [processorSettings?.animalSettings]);

  // Filter inspection levels based on enabled animal settings for selected species
  const inspectionLevelOptions = useMemo(() => {
    const selectedAnimalSetting = processorSettings?.animalSettings?.find(
      (setting) => setting?.species === values?.species && setting?.isEnabled,
    );

    return (
      selectedAnimalSetting?.inspectionLevels?.map((level) => ({
        label: inspectionLevelHelper(level as unknown as string).label,
        value: level as string,
      })) ||
      Object.values(EnumProcessorSchedulingAnimalHeadsInspectionLevel).map(
        (level) => ({
          label: inspectionLevelHelper(level).label,
          value: level,
        }),
      )
    );
  }, [processorSettings?.animalSettings, values?.species]);

  if (!values || !setFieldValue) {
    return <div>Loading...</div>;
  }

  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <Stack spacing={1} direction="row">
        <InputQuantity
          label="Animals"
          required
          nameOverride="quantity"
          min={1}
          max={50}
          size="small"
        />

        <InputSelect
          label="Species"
          required
          nameOverride="species"
          size="small"
          options={speciesOptions}
        />

        <InputSelect
          label="Inspection Level"
          size="small"
          nameOverride="inspectionLevel"
          required
          options={inspectionLevelOptions}
        />
      </Stack>

      <QuickScheduleMiniMonth
        vendorId={vendorId}
        species={values.species}
        selectedDate={values.date ? moment(values.date) : null}
        onDateSelect={(date: Moment) => {
          setFieldValue('date', date.format('YYYY-MM-DD'));
        }}
        onBlockSelect={(block) => {
          setFieldValue('blockTitle', block.blockTitle);
          setFieldValue('blockIndex', block.blockIndex);
        }}
        selectedBlock={{
          blockTitle: values.blockTitle || '',
          blockIndex: values.blockIndex,
        }}
        minDate={moment().add(1, 'day')}
        selectedQuantity={values.quantity || 1}
      />
    </Stack>
  );
}
